import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
	type AttachRdoPhotoInput,
	attachRdoPhoto,
	detachRdoPhoto,
	updateRdoPhoto,
} from "@/adapters/api";
import type { Rdo } from "../types.ts";
import { toRdoPhoto } from "../utils/map.ts";
import { rdoQueryKey } from "./use-rdo.ts";

export function useRdoPhotos(
	constructionId: string | undefined,
	rdoId: string | undefined,
) {
	const queryClient = useQueryClient();
	const key = rdoQueryKey(constructionId, rdoId);

	function requireIds() {
		if (!constructionId || !rdoId) throw new Error("missing rdo");
		return { constructionId, rdoId };
	}

	function invalidate() {
		queryClient.invalidateQueries({ queryKey: key });
	}

	const attach = useMutation({
		mutationFn: async (variables: {
			activityId: string;
			inputs: AttachRdoPhotoInput[];
		}) => {
			const ids = requireIds();
			const photos = [];
			for (const input of variables.inputs) {
				photos.push(
					toRdoPhoto(
						await attachRdoPhoto(
							ids.constructionId,
							ids.rdoId,
							variables.activityId,
							input,
						),
					),
				);
			}
			return photos;
		},
		onSettled: invalidate,
	});

	const updateCaption = useMutation({
		mutationFn: async (variables: { photoId: string; caption: string }) => {
			const ids = requireIds();
			return toRdoPhoto(
				await updateRdoPhoto(
					ids.constructionId,
					ids.rdoId,
					variables.photoId,
					variables.caption,
				),
			);
		},
		onSuccess: (photo) => {
			queryClient.setQueryData<Rdo>(key, (current) =>
				current
					? {
							...current,
							activities: current.activities.map((activity) => ({
								...activity,
								photos: activity.photos.map((item) =>
									item.id === photo.id ? photo : item,
								),
							})),
						}
					: current,
			);
		},
		onError: invalidate,
	});

	const detach = useMutation({
		mutationFn: async (photoId: string) => {
			const ids = requireIds();
			await detachRdoPhoto(ids.constructionId, ids.rdoId, photoId);
		},
		onSuccess: invalidate,
	});

	return { attach, updateCaption, detach };
}
